import React, { useEffect, useRef, useState } from "react";
import "./css-styles/index.css";
import { useNavigate } from "react-router-dom";
import { useSupabaseAuth } from "../hooks/useSupabaseAuth";
import logger from "../services/logger";

const AuthHeroPage = () => {
  const navigate = useNavigate();
  const starsContainerRef = useRef(null);
  const particlesContainerRef = useRef(null);

  // Supabase auth hooks
  const { signIn, signUp } = useSupabaseAuth();

  // Form state
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ENHANCED SPACE THEME - Full space experience with varied stars, nebula, and shooting stars
  // TO REVERT: Replace this entire useEffect with the original version from git history
  useEffect(() => {
    const createStars = () => {
      const starsContainer = starsContainerRef.current;
      if (!starsContainer) return;

      starsContainer.innerHTML = '';
      const starCount = 600;

      for (let i = 0; i < starCount; i++) {
        const star = document.createElement("div");
        star.className = "star";

        // More uniform stars with subtle variation
        const rand = Math.random();
        let size, opacity;

        if (rand > 0.98) {
          // Hero stars (2%) - Large prominent stars with pulsing glow
          size = Math.random() * 1.5 + 4; // 4-5.5px
          opacity = 1;
          star.classList.add('star-pulse');
          star.style.boxShadow = `0 0 10px 4px rgba(255, 255, 255, 0.7), 0 0 16px 6px rgba(135, 206, 250, 0.5)`;
          star.style.animationDelay = `${Math.random() * 3}s`;
        } else if (rand > 0.93) {
          // Bright twinkling stars (5%)
          size = 3;
          opacity = 0.9;
          star.classList.add('star-twinkle');
          star.style.boxShadow = `0 0 4px 1.5px rgba(255, 255, 255, 0.5)`;
          // Random delay to prevent sync (0-4s)
          star.style.animationDelay = `${Math.random() * 4}s`;
        } else if (rand > 0.83) {
          // Medium stars (10%)
          size = 2.5;
          opacity = 0.75;
          star.style.boxShadow = `0 0 3px 1px rgba(255, 255, 255, 0.3)`;
        } else {
          // Standard stars (83%)
          size = 1.5;
          opacity = Math.random() * 0.3 + 0.5; // 0.5-0.8
        }

        star.style.width = `${size}px`;
        star.style.height = `${size}px`;
        star.style.left = `${Math.random() * 100}%`;
        star.style.top = `${Math.random() * 300}%`;
        star.style.opacity = opacity;
        star.style.borderRadius = '50%';

        starsContainer.appendChild(star);
      }

      // Add nebula gradient overlay
      const nebula = document.createElement("div");
      nebula.className = "nebula-overlay";
      starsContainer.appendChild(nebula);
    };

    const createParticles = () => {
      const particlesContainer = particlesContainerRef.current;
      if (!particlesContainer) return;
    
      particlesContainer.innerHTML = '';
      const particleCount = 30;
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement("div");
        particle.className = "particle";
        const size = Math.random() * 8 + 2;
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        
        const initialLeft = Math.random() * 100;
        const initialTop = Math.random() * 100;
        particle.style.left = `${initialLeft}%`;
        particle.style.top = `${initialTop}%`;
        particle.style.opacity = `${Math.random() * 0.5 + 0.3}`;
        particle.style.animation = `float ${Math.random() * 15 + 10}s ${Math.random() * 2}s infinite linear`;
        particle.setAttribute('data-parallax-multiplier', Math.random() * 0.3 + 0.1);
        particlesContainer.appendChild(particle);
      }
    };

    const handleParallax = () => {
      const starsContainer = starsContainerRef.current;
      const particlesContainer = particlesContainerRef.current;
      
      if (starsContainer) {
        const scrollPosition = window.scrollY;
        starsContainer.style.transform = `translateY(${scrollPosition * 0.2}px)`;
      }

      if (particlesContainer) {
        const scrollPosition = window.scrollY;
        const particles = particlesContainer.children;
        
        for (let particle of particles) {
          const multiplier = parseFloat(particle.getAttribute('data-parallax-multiplier'));
          const parallaxTransform = `translate(0, ${scrollPosition * multiplier}px)`;
          particle.style.transform = parallaxTransform;
        }
      }
    };

    const scrollRevealElements = document.querySelectorAll("[data-scroll-reveal]");
    const isInViewport = (element) => {
      const rect = element.getBoundingClientRect();
      return rect.top <= (window.innerHeight || document.documentElement.clientHeight) * 0.75;
    };

    const handleScrollReveal = () => {
      scrollRevealElements.forEach((el) => {
        if (isInViewport(el) && !el.classList.contains("appear")) {
          el.classList.add("appear");
        }
      });
    };

    // Shooting stars - create one every 8-15 seconds with random directions
    const createShootingStar = () => {
      const starsContainer = starsContainerRef.current;
      if (!starsContainer) return;

      const shootingStar = document.createElement("div");

      // Random direction: diagonal-down, diagonal-up, or arc-right
      const directions = ['diagonal-down', 'diagonal-up', 'arc-right'];
      const randomDirection = directions[Math.floor(Math.random() * directions.length)];
      shootingStar.className = `shooting-star ${randomDirection}`;

      // Random starting position based on direction
      if (randomDirection === 'diagonal-down') {
        // Top-left area
        shootingStar.style.left = `${Math.random() * 30}%`;
        shootingStar.style.top = `${Math.random() * 30}%`;
      } else if (randomDirection === 'diagonal-up') {
        // Bottom-left area
        shootingStar.style.left = `${Math.random() * 30}%`;
        shootingStar.style.top = `${Math.random() * 30 + 50}%`;
      } else {
        // arc-right: Top area
        shootingStar.style.left = `${Math.random() * 20}%`;
        shootingStar.style.top = `${Math.random() * 40}%`;
      }

      starsContainer.appendChild(shootingStar);

      // Remove after animation completes (2.2s for longest animation)
      setTimeout(() => {
        shootingStar.remove();
      }, 2500);
    };

    // Schedule random shooting stars
    const scheduleShootingStar = () => {
      const delay = Math.random() * 7000 + 8000; // 8-15 seconds
      setTimeout(() => {
        createShootingStar();
        scheduleShootingStar(); // Schedule next one
      }, delay);
    };

    createStars();
    createParticles();
    scheduleShootingStar(); // Start shooting star cycle
    window.addEventListener("scroll", handleScrollReveal);
    window.addEventListener("scroll", handleParallax);

    return () => {
      window.removeEventListener("scroll", handleScrollReveal);
      window.removeEventListener("scroll", handleParallax);
    };
  }, []);

  // Handle form submission - Supabase auth with bridge
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let result;

      if (isLogin) {
        // Sign in with Supabase
        result = await signIn(formData.email, formData.password);
      } else {
        // Sign up with Supabase
        const metadata = {
          display_name: formData.username || formData.email.split('@')[0]
        };
        result = await signUp(formData.email, formData.password, metadata);
      }

      if (result.error) {
        setError(result.error.message || 'Authentication failed');
        setLoading(false);
        return;
      }

      // Check if email needs confirmation (production mode)
      const emailConfirmed = result.data.user.email_confirmed_at;
      const hasSession = !!result.data.session?.access_token;

      if (!isLogin && !emailConfirmed) {
        // Signup without email confirmation - show message and redirect to login
        setError('✅ Account created! Please check your email to confirm your account before signing in.');
        setIsLogin(true); // Switch to login mode
        setLoading(false);
        return;
      }

      if (!hasSession) {
        // No active session (shouldn't happen with email confirmation disabled)
        setError('Please check your email to confirm your account, then sign in.');
        setIsLogin(true);
        setLoading(false);
        return;
      }

      // Success! Session is active, navigate to platforms
      logger.success('Auth successful:', result.data.user.email);
      navigate('/platforms');

    } catch (err) {
      logger.error('Auth error:', err);
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="hero-page">
      <div className="page-background"></div>
      <div ref={starsContainerRef} className="stars"></div>
      <div ref={particlesContainerRef} className="particles"></div>

      {/* Navigation */}
      <nav>
        <div className="logo">Phantom</div>
        <ul className="nav-links">
          <li><a href="#features">Features</a></li>
          <li><a href="#how-it-works">How It Works</a></li>
          <li><a href="#auth">Get Started</a></li>
        </ul>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="rings"></div>
        <h1>
          Phantom
          <span style={{
            marginLeft: '12px',
            padding: '4px 12px',
            fontSize: '14px',
            fontWeight: '600',
            background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.2), rgba(29, 185, 84, 0.2))',
            border: '1px solid rgba(0, 217, 255, 0.4)',
            borderRadius: '20px',
            color: '#00D9FF',
            verticalAlign: 'middle'
          }}>BETA</span>
        </h1>
        <h2 className="tagline">The voice in your music</h2>
        <p>
          Experience personalized music discovery with an AI DJ that learns your taste,
          seamlessly works across platforms, and creates the perfect soundtrack for every moment.
        </p>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.6)',
          marginTop: '8px'
        }}>
          Early access - Starting with Spotify Premium
        </p>
        <a href="#features" className="btn">Discover Features</a>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="rings" style={{ top: "-300px", left: "-300px" }}></div>
        <h2>Revolutionary Music Experience</h2>
        <p>Powered by advanced AI, designed for music lovers</p>

        <div className="features-grid" data-scroll-reveal>
          <div className="feature-card" data-scroll-reveal>
            <div className="feature-icon">🎙️</div>
            <h3>AI DJ Host</h3>
            <p>
              Your personal AI DJ introduces tracks, shares insights, and creates 
              smooth transitions while learning your unique music taste.
            </p>
          </div>

          <div className="feature-card" data-scroll-reveal>
            <div className="feature-icon">🔄</div>
            <h3>Cross-Platform Discovery</h3>
            <p>
              Seamlessly switch between Spotify, YouTube Music, and more. 
              Your preferences and AI learning work across all platforms.
            </p>
          </div>

          <div className="feature-card" data-scroll-reveal>
            <div className="feature-icon">🎵</div>
            <h3>Smart Continuation</h3>
            <p>
              Never run out of music. Our AI automatically adds perfect tracks 
              to keep your vibe going, learning from every song you play.
            </p>
          </div>

          <div className="feature-card" data-scroll-reveal>
            <div className="feature-icon">🧠</div>
            <h3>Intelligent Recommendations</h3>
            <p>
              Advanced algorithms analyze your listening patterns, mood, and context 
              to suggest music that perfectly matches your current vibe.
            </p>
          </div>

          <div className="feature-card" data-scroll-reveal>
            <div className="feature-icon">⚡</div>
            <h3>Real-Time Adaptation</h3>
            <p>
              AI that adapts to your energy levels, time of day, and listening context 
              to deliver the perfect soundtrack for every moment.
            </p>
          </div>

          <div className="feature-card" data-scroll-reveal>
            <div className="feature-icon">🎯</div>
            <h3>Personalized Experience</h3>
            <p>
              Every interaction teaches the AI more about you. Create multiple profiles, 
              save preferences, and enjoy music that truly understands you.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <h2>How It Works</h2>
        <div className="steps-container" data-scroll-reveal>
          <div className="step" data-scroll-reveal>
            <div className="step-number">1</div>
            <h3>Create Your Profile</h3>
            <p>Sign up and tell us about your music preferences to get started.</p>
          </div>
          
          <div className="step" data-scroll-reveal>
            <div className="step-number">2</div>
            <h3>Connect Your Platform</h3>
            <p>Link your Spotify, YouTube Music, or other streaming accounts.</p>
          </div>
          
          <div className="step" data-scroll-reveal>
            <div className="step-number">3</div>
            <h3>Let AI Learn</h3>
            <p>Start playing music and let our AI DJ learn your unique taste.</p>
          </div>
          
          <div className="step" data-scroll-reveal>
            <div className="step-number">4</div>
            <h3>Enjoy Personalized Music</h3>
            <p>Experience perfectly curated playlists and intelligent recommendations.</p>
          </div>
        </div>
      </section>

      {/* Auth Section */}
      <section id="auth" className="auth-section">
        <div className="auth-container">
          <div className="auth-card">
            {/* Modern Tab Toggle */}
            <div className="auth-tabs">
              <button
                type="button"
                className={`auth-tab ${isLogin ? 'active' : ''}`}
                onClick={() => {
                  setIsLogin(true);
                  setError('');
                  setFormData({ email: '', password: '', username: '' });
                }}
                disabled={loading}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`auth-tab ${!isLogin ? 'active' : ''}`}
                onClick={() => {
                  setIsLogin(false);
                  setError('');
                  setFormData({ email: '', password: '', username: '' });
                }}
                disabled={loading}
              >
                Sign Up
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ marginTop: '2rem' }}>
              {!isLogin && (
                <div style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    name="username"
                    placeholder="Username (optional)"
                    value={formData.username}
                    onChange={handleInputChange}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '16px'
                    }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px'
                  }}
                />
              </div>

              {error && (
                <div style={{
                  padding: '12px',
                  backgroundColor: error.startsWith('✅') ? 'rgba(29, 185, 84, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                  border: error.startsWith('✅') ? '1px solid rgba(29, 185, 84, 0.3)' : '1px solid rgba(220, 53, 69, 0.3)',
                  borderRadius: '8px',
                  color: error.startsWith('✅') ? '#1db954' : '#ff6b6b',
                  marginBottom: '1rem',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="auth-button"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
              </button>
            </form>

            <p style={{ marginTop: '1.5rem', color: '#999', fontSize: '14px' }}>
              Secure authentication powered by Supabase
            </p>
          </div>
        </div>
      </section>


    </div>
  );
};

export default AuthHeroPage;